import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Trees, X } from "lucide-react";

const HIDE_EXACT = new Set(["/", "/home", "/dashboard", "/waitlist", "/waitlist-confirmation", "/login", "/signup"]);

// Friendly names for the parent section a drill-in page returns to.
const SECTION_LABELS = {
  "parent-corner": "Parent Corner",
  learn: "Learn",
  arcade: "Arcade",
  adventures: "Adventures",
  create: "Create",
  dashboard: "Dashboard",
  academy: "Academy",
};

const BTN_BASE = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  minHeight: 40,
  borderRadius: 9999,
  border: "1px solid rgba(255,255,255,0.25)",
  color: "#fff",
  fontSize: 13,
  fontWeight: 800,
  fontFamily: "'Fredoka', sans-serif",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

export default function GlobalBackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  // Always sit just below the LIVE header + banner. We reuse the exact same CSS
  // variables the fixed header itself is positioned with (`--app-banner-h` kept
  // live by PreviewBannerBar, `--app-header-h` kept live by Header's
  // ResizeObserver), so the button can NEVER end up behind the header — even
  // when the Fall banner loads in late or the header height changes on resize.
  // (The old JS getBoundingClientRect measurement had a 124px fallback that
  // raced the async banner and left the button hidden on production.)
  const TOP_CSS = "calc(var(--app-banner-h, 0px) + var(--app-header-h, 116px) + 10px)";
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  // Users can tuck the Back button away for a few minutes when it's in the way.
  const DISMISS_MS = 3 * 60 * 1000;
  const [hiddenUntil, setHiddenUntil] = useState(() => {
    try { return parseInt(sessionStorage.getItem("gbb_hidden_until") || "0", 10) || 0; } catch { return 0; }
  });
  // Position preference removed — the button stays top-left.
  const [pos] = useState("left");
  // A full-screen overlay/modal (e.g. the curriculum skill/lesson modal) asks us
  // to step aside so we never sit on top of its title.
  const [overlayOpen, setOverlayOpen] = useState(false);
  useEffect(() => {
    const h = (e) => setOverlayOpen(!!(e && e.detail));
    window.addEventListener("iw-overlay", h);
    return () => window.removeEventListener("iw-overlay", h);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!hiddenUntil) return;
    const remaining = hiddenUntil - Date.now();
    if (remaining <= 0) { setHiddenUntil(0); return; }
    const t = setTimeout(() => setHiddenUntil(0), remaining);
    return () => clearTimeout(t);
  }, [hiddenUntil]);

  const dismissAwhile = () => {
    const until = Date.now() + DISMISS_MS;
    try { sessionStorage.setItem("gbb_hidden_until", String(until)); } catch {}
    setHiddenUntil(until);
  };

  // Measure removed — positioning now driven purely by the header/banner CSS
  // variables (see TOP_CSS above), which are always current.

  // Tell the layout when the floating back bar is actually on screen so mobile
  // content can reserve space and the bar never sits on top of a page title.
  useEffect(() => {
    const hidden = HIDE_EXACT.has(path)
      || overlayOpen
      || path.startsWith("/academy")
      || path.startsWith("/learn/academy")
      || (hiddenUntil && hiddenUntil > Date.now());
    document.body.classList.toggle("gbb-visible", !hidden);
    return () => document.body.classList.remove("gbb-visible");
  }, [path, hiddenUntil, overlayOpen]);

  if (overlayOpen) return null; // a modal is open — don't overlap its header
  if (HIDE_EXACT.has(path)) return null;
  if (path.startsWith("/academy") || path.startsWith("/learn/academy")) return null; // academy has its own exit
  if (hiddenUntil && hiddenUntil > Date.now()) {
    // Tucked away: show only a tiny peek tab to bring it back.
    return (
      <button
        onClick={() => { try { sessionStorage.removeItem("gbb_hidden_until"); } catch {} ; setHiddenUntil(0); }}
        data-testid="global-back-restore"
        aria-label="Show back button"
        style={{ position: "fixed", top: TOP_CSS, left: 0, zIndex: 45, padding: "6px 8px 6px 4px",
          borderTopRightRadius: 9999, borderBottomRightRadius: 9999, border: "1px solid rgba(255,255,255,0.2)", borderLeft: "none",
          background: "linear-gradient(135deg,#F59E0B,#F97316)", color: "#fff", opacity: 0.85, cursor: "pointer" }}
      >
        <ArrowLeft size={14} />
      </button>
    );
  }

  const parts = path.replace(/\/+$/, "").split("/").filter(Boolean);
  const parentSeg = parts.length > 1 ? parts[parts.length - 2] : null;
  // Any Learning Forest tool page (e.g. /learn/tutor, /learn/whiteboard, …) but
  // NOT the Learning Forest home itself.
  const inLearn = path.startsWith("/learn") && path !== "/learn";

  // Always show a "previous page" back. The label just names the parent section
  // when we can infer one; the action is always navigate(-1).
  const backLabel = inLearn
    ? "Back"
    : (parentSeg && SECTION_LABELS[parentSeg] ? `Back to ${SECTION_LABELS[parentSeg]}` : "Back");

  const goBack = () => {
    if (window.history.length > 2) { navigate(-1); return; }
    if (parts.length > 1) navigate("/" + parts.slice(0, -1).join("/"));
    else navigate("/dashboard");
  };

  const horizStyle = pos === "left"
    ? { left: 12, right: "auto", justifyContent: "flex-start" }
    : pos === "right"
    ? { right: 12, left: "auto", justifyContent: "flex-end" }
    : { left: "50%", transform: "translateX(-50%)", justifyContent: "center" };

  // Compact on phones: icon + short label, smaller padding.
  const compactBase = isMobile
    ? { ...BTN_BASE, padding: "5px 10px", minHeight: 32, fontSize: 12, gap: 4 }
    : BTN_BASE;
  const shortBack = isMobile ? "Back" : backLabel;

  return (
    <div
      style={{ position: "fixed", top: TOP_CSS, zIndex: 45, display: "flex", alignItems: "center", gap: 6, ...horizStyle }}
    >
      {/* Orange, step back to the previous page (browser history) */}
      <button
        onClick={goBack}
        data-testid="global-back-button"
        aria-label={backLabel}
        style={{
          ...compactBase,
          boxShadow: "0 4px 14px rgba(249,115,22,0.4)",
          background: "linear-gradient(135deg,#F59E0B,#F97316)",
        }}
      >
        <ArrowLeft size={isMobile ? 14 : 15} />
        {shortBack}
      </button>

      {/* Blue, jump straight to the Learning Forest home (only on learn tools) */}
      {inLearn && (
        <button
          onClick={() => navigate("/learn")}
          data-testid="global-forest-button"
          aria-label="Back to Learning Forest"
          title="Go to the Learning Forest to pick another tool"
          style={{
            ...compactBase,
            boxShadow: "0 4px 14px rgba(37,99,235,0.4)",
            background: "linear-gradient(135deg,#3B82F6,#2563EB)",
          }}
        >
          <Trees size={isMobile ? 14 : 15} />
          {isMobile ? "Forest" : "Learning Forest"}
        </button>
      )}

      {/* Tuck-away (X for 3 min) — tap the peek tab to bring it back. */}
      <button
        onClick={dismissAwhile}
        data-testid="global-back-dismiss"
        aria-label="Hide back button for a few minutes"
        title="Hide for 3 minutes"
        style={{ ...compactBase, padding: isMobile ? "5px 7px" : "8px 9px", background: "rgba(0,0,0,0.5)", boxShadow: "none" }}
      >
        <X size={isMobile ? 12 : 13} />
      </button>
    </div>
  );
}
